import { useState } from "react";
import { TailSpin } from "react-loader-spinner";

import useSubmitObjection from "../../customHooks/useSubmitObjection";
import { compareFileSize, getShortFileName } from "../../libs/generalFunctions";

// Shares the create modal's design system so every case popup looks the same.
import "../AddDisciplinaryCasePopup/AddDisciplinaryCasePopup.scss";

// Mirror the backend rule (evidence.* mimes / max:5 / 5 MB).
const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx";
const MAX_FILES = 5;
const MAX_MB = 5;

export default function SubmitObjectionPopup({ caseEmployeeId, onClose, onSuccess }) {
  const { submitObjection, loading } = useSubmitObjection();

  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");

  const handleFiles = (e) => {
    setError("");
    const picked = Array.from(e.target.files || []);
    e.target.value = ""; // let the same file be re-picked after a remove
    const next = [...files];

    for (const file of picked) {
      if (next.length >= MAX_FILES) {
        setError(`يمكن إرفاق ${MAX_FILES} ملفات كحد أقصى`);
        break;
      }
      if (!compareFileSize(file.size, MAX_MB)) {
        setError(`حجم كل ملف يجب ألا يتجاوز ${MAX_MB} ميغابايت`);
        continue;
      }
      next.push(file);
    }

    setFiles(next);
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setError("يرجى كتابة سبب الاعتراض");
      return;
    }

    const res = await submitObjection(caseEmployeeId, {
      reason: reason.trim(),
      files,
    });

    if (res?.success) {
      onSuccess?.();
      onClose();
    }
  };

  return (
    <div
      className="case-modal__overlay"
      onClick={(e) => e.target.classList.contains("case-modal__overlay") && onClose()}
    >
      <div className="case-modal__panel" dir="rtl">

        {/* HEADER */}
        <div className="case-modal__header">
          <h3 className="case-modal__title">تقديم اعتراض</h3>
          <button type="button" className="case-modal__close" onClick={onClose} aria-label="إغلاق">
            ✕
          </button>
        </div>

        {/* BODY */}
        <div className="case-modal__body">
          <div className="case-modal__section">
            <div className="case-modal__recommended case-modal__hint">
              يمكنك الاعتراض على العقوبة خلال 7 أيام من صدور القرار النهائي. اكتب سبب الاعتراض
              وأرفق ما يدعمه (اختياري).
            </div>

            <div className="case-modal__field">
              <label className="case-modal__label">سبب الاعتراض</label>
              <textarea
                className="case-modal__textarea"
                placeholder="اشرح سبب اعتراضك على العقوبة"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>

            <div className="case-modal__field">
              <label className="case-modal__label">
                المرفقات{" "}
                <span className="case-modal__optional">
                  (اختياري — حتى {MAX_FILES} ملفات، {MAX_MB}MB لكل ملف)
                </span>
              </label>
              <input
                type="file"
                className="case-modal__input"
                accept={ACCEPT}
                multiple
                onChange={handleFiles}
              />

              {files.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                  {files.map((file, i) => (
                    <span
                      key={`${file.name}-${i}`}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        background: "#f1f5f9",
                        border: "1px solid #e2e8f0",
                        borderRadius: "6px",
                        padding: "4px 8px",
                        fontSize: "13px",
                        color: "#0f172a",
                      }}
                    >
                      {getShortFileName(file.name)}
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        style={{
                          border: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#ef4444",
                          fontWeight: "bold",
                          lineHeight: 1,
                        }}
                        aria-label="حذف الملف"
                      >
                        ✕
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {error && <div className="case-modal__error">{error}</div>}
          </div>
        </div>

        {/* FOOTER */}
        <div className="case-modal__footer case-modal__footer--duo">
          <button type="button" className="case-modal__btn--ghost" onClick={onClose} disabled={loading}>
            إلغاء
          </button>
          <button
            type="button"
            className="case-modal__btn--primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <TailSpin height="18" width="18" color="#fff" /> : "تقديم الاعتراض"}
          </button>
        </div>

      </div>
    </div>
  );
}
